import { useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useCallCentersStore } from "@/store/call-centers/call-centers.store";
import { TCallCenter } from "@/models/call-centers.model";

const SELECTED_QUERY_PARAM = "selected";

export const useCallCenters = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const callCenters = useCallCentersStore((s) => s.callCenters);
  const selectedCallCenter = useCallCentersStore((s) => s.selectedCallCenter);
  const selectedCallCenterLoading = useCallCentersStore((s) => s.selectedCallCenterLoading);
  const loading = useCallCentersStore((s) => s.loading);
  const fetchCallCenters = useCallCentersStore((s) => s.fetchCallCenters);
  const fetchCallCenter = useCallCentersStore((s) => s.fetchCallCenter);
  const setSelectedCallCenter = useCallCentersStore((s) => s.setSelectedCallCenter);
  const setSelectionReady = useCallCentersStore((s) => s.setSelectionReady);

  const fetchOneAbortRef = useRef<AbortController | null>(null);
  const fetchListAbortRef = useRef<AbortController | null>(null);
  const restoredFromUrlRef = useRef(false);
  const selectionReady = useCallCentersStore((s) => s.selectionReady);

  useEffect(() => {
    fetchListAbortRef.current?.abort();
    const controller = new AbortController();
    fetchListAbortRef.current = controller;
    fetchCallCenters(controller.signal);
    return () => {
      controller.abort();
    };
  }, [fetchCallCenters]);

  const updateSelectedQueryParam = useCallback(
    (id: number | null) => {
      const next = new URLSearchParams(searchParams);
      if (id === null) {
        next.delete(SELECTED_QUERY_PARAM);
      } else {
        next.set(SELECTED_QUERY_PARAM, String(id));
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const selectCallCenter = useCallback(
    (callCenter: TCallCenter | null) => {
      fetchOneAbortRef.current?.abort();

      if (!callCenter) {
        setSelectedCallCenter(null);
        updateSelectedQueryParam(null);
        return;
      }

      setSelectedCallCenter(callCenter);
      updateSelectedQueryParam(callCenter.id);
      const controller = new AbortController();
      fetchOneAbortRef.current = controller;
      fetchCallCenter(callCenter.id, controller.signal);
    },
    [setSelectedCallCenter, fetchCallCenter, updateSelectedQueryParam]
  );

  // Restore selection from the `?selected=` query param once the list arrives.
  // Falling back to the first call center is handled in the store's fetch action.
  // selectionReady is set to true once this runs so dependents know the final
  // selection is stable and can safely trigger their own data fetches.
  useEffect(() => {
    if (restoredFromUrlRef.current || callCenters.length === 0) return;
    const idParam = searchParams.get(SELECTED_QUERY_PARAM);
    const idFromUrl = idParam ? Number(idParam) : NaN;
    if (Number.isFinite(idFromUrl)) {
      const target = callCenters.find((cc) => cc.id === idFromUrl);
      if (target) {
        selectCallCenter(target);
      } else {
        updateSelectedQueryParam(null);
      }
    }
    restoredFromUrlRef.current = true;
    setSelectionReady(true);
  }, [callCenters, searchParams, selectCallCenter, updateSelectedQueryParam, setSelectionReady]);

  return {
    callCenters,
    selectedCallCenter,
    selectedCallCenterLoading,
    loading,
    selectionReady,
    selectCallCenter,
  };
};
